import React, { useState, useEffect } from 'react';
import { Container, Form, Modal, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faEye, faEyeSlash,
  faUserShield, faUser, faShield, faCheck
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import '../styles/modal-shared.css';

const MODULES = {
  transportation:    { label: 'Transportation (BOLs)',  permissions: ['full_access'],              exclusive: false },
  warehouse_receipts:{ label: 'Warehouse Receipts',     permissions: ['full_access'],              exclusive: false },
  inventory:         { label: 'Inventory',              permissions: ['read_only','submit','approve'], exclusive: true },
  accounts:          { label: 'Accounts Management',   permissions: ['full_access'],              exclusive: false },
  invoices:          { label: 'Invoices',               permissions: ['full_access'],              exclusive: false },
  ext_orders:        { label: 'External Orders',        permissions: ['full_access'],              exclusive: false }
};

const PERM_LABELS = {
  full_access: 'Full Access',
  read_only:   'Read Only',
  submit:      'Submit Orders',
  approve:     'Approve (Full)'
};

function Users() {
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [modalMode, setModalMode]       = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab]       = useState('basic');
  const [formData, setFormData]         = useState({
    username: '', email: '', password: '',
    firstName: '', lastName: '',
    isAdmin: false, isActive: true, permissions: {}
  });

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (showModal) { setActiveTab('basic'); setShowPassword(false); }
  }, [showModal]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setModalMode('add');
    setSelectedUser(null);
    setFormData({ username:'', email:'', password:'', firstName:'', lastName:'', isAdmin:false, isActive:true, permissions:{} });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username, email: user.email, password: '',
      firstName: user.firstName, lastName: user.lastName,
      isAdmin: user.isAdmin, isActive: user.isActive,
      permissions: user.permissions || {}
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.username}"?`)) return;
    try {
      await api.delete(`/api/users/${user.id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (modalMode === 'edit' && !payload.password) delete payload.password;
      if (modalMode === 'add') await api.post('/api/users', payload);
      else await api.put(`/api/users/${selectedUser.id}`, payload);
      toast.success(`User ${modalMode === 'add' ? 'created' : 'updated'} successfully`);
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handlePermissionChange = (module, permission, checked) => {
    setFormData(prev => {
      const newPermissions = { ...prev.permissions };
      const moduleConfig = MODULES[module];
      if (moduleConfig?.exclusive) {
        if (checked) newPermissions[module] = [permission];
        else delete newPermissions[module];
      } else {
        if (checked) {
          if (!newPermissions[module]) newPermissions[module] = [];
          if (!newPermissions[module].includes(permission)) newPermissions[module].push(permission);
        } else {
          if (newPermissions[module]) {
            newPermissions[module] = newPermissions[module].filter(p => p !== permission);
            if (newPermissions[module].length === 0) delete newPermissions[module];
          }
        }
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const renderPermissions = (permissions) => {
    if (!permissions || Object.keys(permissions).length === 0)
      return <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>None</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {Object.entries(permissions).map(([module, perms]) => (
          <span key={module} style={{
            background: '#eff6ff', color: '#1d4ed8', fontSize: '0.68rem', fontWeight: 600,
            padding: '2px 7px', borderRadius: 9999, border: '1px solid #bfdbfe'
          }}>
            {MODULES[module]?.label || module}: {perms.map(p => PERM_LABELS[p] || p).join(', ')}
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <Spinner animation="border" style={{ color: 'var(--tenant-primary-color, #3b82f6)' }} />
      </div>
    );
  }

  const showPermissionsTab = !formData.isAdmin;

  return (
    <Container fluid className="p-3">
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
            <FontAwesomeIcon icon={faUserShield} style={{ marginRight: 10, color: 'var(--tenant-primary-color, #3b82f6)' }} />
            Portal Users
          </h2>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '2px 0 0' }}>
            Manage sub-user access and permissions
          </p>
        </div>
        <button className="detail-btn detail-btn--primary" onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} />
          Add User
        </button>
      </div>

      {/* Users table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['User', 'Name', 'Email', 'Role', 'Permissions', 'Status', 'Last Login', ''].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', fontSize: '0.68rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: '#64748b', whiteSpace: 'nowrap', textAlign: 'left'
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>
                  <FontAwesomeIcon
                    icon={user.isAdmin ? faUserShield : faUser}
                    style={{ marginRight: 8, color: user.isAdmin ? '#ef4444' : '#94a3b8', fontSize: '0.8rem' }}
                  />
                  {user.username}
                </td>
                <td style={{ padding: '10px 14px', color: '#374151' }}>{user.firstName} {user.lastName}</td>
                <td style={{ padding: '10px 14px', color: '#64748b' }}>{user.email}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    background: user.isAdmin ? '#fef2f2' : '#f1f5f9',
                    color: user.isAdmin ? '#b91c1c' : '#475569',
                    fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 9999
                  }}>
                    {user.isAdmin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', maxWidth: 280 }}>
                  {user.isAdmin
                    ? <span style={{ background: '#f0fdf4', color: '#065f46', fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 9999 }}>All Permissions</span>
                    : renderPermissions(user.permissions)
                  }
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    background: user.isActive ? '#f0fdf4' : '#f8fafc',
                    color: user.isActive ? '#065f46' : '#94a3b8',
                    fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 9999
                  }}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="detail-btn detail-btn--icon" onClick={() => handleEdit(user)} title="Edit">
                      <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.78rem' }} />
                    </button>
                    <button
                      className="detail-btn detail-btn--icon"
                      onClick={() => handleDelete(user)}
                      title="Delete"
                      style={{ color: '#ef4444', borderColor: '#fecaca' }}
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.78rem' }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                  No users yet. Click "Add User" to create the first one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── User Modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" dialogClassName="detail-modal" centered>
        <Modal.Header closeButton>
          <div className="modal-title-row">
            <div className="modal-header-icon">
              <FontAwesomeIcon icon={modalMode === 'add' ? faUser : faEdit} />
            </div>
            <div className="modal-header-text">
              <h5>{modalMode === 'add' ? 'Add New User' : 'Edit User'}</h5>
              {modalMode === 'edit' && selectedUser && (
                <p className="modal-subtitle">{selectedUser.username} · {selectedUser.firstName} {selectedUser.lastName}</p>
              )}
            </div>
          </div>
        </Modal.Header>

        <Form onSubmit={handleSubmit} autoComplete="off">
          <Modal.Body style={{ padding: 0, background: '#f8fafc' }}>
            {/* Tab bar */}
            <div style={{
              display: 'flex', borderBottom: '1px solid #e2e8f0',
              background: '#fff', padding: '0 20px'
            }}>
              {[
                { key: 'basic',       label: 'Basic Information', icon: faUser   },
                ...(!formData.isAdmin ? [{ key: 'permissions', label: 'Permissions', icon: faShield }] : [])
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '12px 16px', border: 'none', background: 'transparent',
                    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                    color: activeTab === tab.key ? 'var(--tenant-primary-color, #3b82f6)' : '#64748b',
                    borderBottom: activeTab === tab.key
                      ? '2px solid var(--tenant-primary-color, #3b82f6)'
                      : '2px solid transparent',
                    marginBottom: -1, transition: 'color 0.15s, border-color 0.15s'
                  }}
                >
                  <FontAwesomeIcon icon={tab.icon} style={{ fontSize: '0.75rem' }} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ padding: 20 }}>

              {/* ── BASIC INFO ── */}
              {activeTab === 'basic' && (
                <div>
                  <input type="text" style={{ display: 'none' }} aria-hidden="true" />
                  <input type="password" style={{ display: 'none' }} aria-hidden="true" />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className="co-label">Username *</div>
                      <Form.Control size="sm" type="text" name="username"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required disabled={modalMode === 'edit'}
                        autoComplete="off" data-lpignore="true"
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <div className="co-label">First Name *</div>
                      <Form.Control size="sm" type="text" name="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required autoComplete="off" data-lpignore="true"
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <div className="co-label">Last Name *</div>
                      <Form.Control size="sm" type="text" name="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required autoComplete="off" data-lpignore="true"
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className="co-label">Email *</div>
                      <Form.Control size="sm" type="email" name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required autoComplete="off" data-lpignore="true"
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className="co-label">
                        Password
                        {modalMode === 'add' && ' *'}
                        {modalMode === 'edit' && <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#94a3b8', marginLeft: 6 }}>leave blank to keep current</span>}
                      </div>
                      <InputGroup size="sm">
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          name="new-password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required={modalMode === 'add'}
                          placeholder={modalMode === 'edit' ? 'Leave blank to keep current' : ''}
                          autoComplete="new-password" data-lpignore="true"
                          style={{ borderRadius: '8px 0 0 8px' }}
                        />
                        <button type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            padding: '0 12px', border: '1px solid #dee2e6', borderLeft: 'none',
                            borderRadius: '0 8px 8px 0', background: '#f8fafc',
                            color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center'
                          }}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} style={{ fontSize: '0.82rem' }} />
                        </button>
                      </InputGroup>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
                        Min 8 chars · uppercase · lowercase · number · special (!@#$%^&amp;*)
                      </div>
                    </div>
                  </div>

                  {/* Toggle cards */}
                  <div style={{ display: 'flex', gap: 12 }}>
                    {[
                      { key: 'isAdmin',  label: 'Admin User', desc: 'Has all permissions',  icon: faUserShield, color: '#ef4444' },
                      { key: 'isActive', label: 'Active',     desc: 'Can log in to portal', icon: faCheck,      color: '#059669' }
                    ].map(({ key, label, desc, icon, color }) => (
                      <label key={key} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        flex: 1, padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                        background: formData[key] ? `color-mix(in srgb, ${color} 8%, #fff)` : '#fff',
                        border: `1.5px solid ${formData[key] ? `color-mix(in srgb, ${color} 30%, transparent)` : '#e2e8f0'}`,
                        transition: 'all 0.15s', userSelect: 'none'
                      }}>
                        <input type="checkbox" checked={formData[key]}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                          style={{ display: 'none' }}
                        />
                        <div style={{
                          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                          background: formData[key] ? color : '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.15s'
                        }}>
                          {formData[key] && <FontAwesomeIcon icon={icon} style={{ color: '#fff', fontSize: '0.6rem' }} />}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{label}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {formData.isAdmin && (
                    <div style={{
                      marginTop: 12, padding: '10px 14px', borderRadius: 10,
                      background: '#fef2f2', border: '1px solid #fecaca',
                      fontSize: '0.78rem', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 8
                    }}>
                      <FontAwesomeIcon icon={faUserShield} />
                      Admin users bypass all permission checks and have full access to everything.
                    </div>
                  )}
                </div>
              )}

              {/* ── PERMISSIONS ── */}
              {activeTab === 'permissions' && showPermissionsTab && (
                <div>
                  <div style={{
                    padding: '10px 14px', borderRadius: 10, marginBottom: 16,
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    fontSize: '0.78rem', color: '#1d4ed8', lineHeight: 1.6
                  }}>
                    <strong>Inventory is hierarchical:</strong> Approve ⊃ Submit ⊃ Read Only. All other modules use Full Access.
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {Object.entries(MODULES).map(([moduleKey, moduleConfig]) => {
                      const hasAccess = !!formData.permissions[moduleKey];
                      return (
                        <div key={moduleKey} style={{
                          background: hasAccess ? 'color-mix(in srgb, var(--tenant-primary-color,#3b82f6) 5%, #fff)' : '#fff',
                          border: `1.5px solid ${hasAccess ? 'color-mix(in srgb, var(--tenant-primary-color,#3b82f6) 25%, transparent)' : '#e2e8f0'}`,
                          borderRadius: 10, padding: '12px 14px', transition: 'all 0.15s'
                        }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>
                            {moduleConfig.label}
                            {moduleConfig.exclusive && (
                              <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.7rem', marginLeft: 6 }}>pick one</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {moduleConfig.permissions.map(permission => {
                              const isChecked = formData.permissions[moduleKey]?.includes(permission) || false;
                              return (
                                <label key={`${moduleKey}-${permission}`}
                                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                                >
                                  <input
                                    type={moduleConfig.exclusive ? 'radio' : 'checkbox'}
                                    name={moduleConfig.exclusive ? `${moduleKey}-permission` : undefined}
                                    checked={isChecked}
                                    onChange={(e) => handlePermissionChange(moduleKey, permission, e.target.checked)}
                                    style={{ accentColor: 'var(--tenant-primary-color, #3b82f6)', width: 14, height: 14, flexShrink: 0 }}
                                  />
                                  <span style={{ fontSize: '0.78rem', color: isChecked ? '#1e293b' : '#64748b', fontWeight: isChecked ? 600 : 400 }}>
                                    {PERM_LABELS[permission] || permission}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {Object.keys(formData.permissions).length === 0 && (
                    <div style={{
                      marginTop: 14, padding: '10px 14px', borderRadius: 10,
                      background: '#fefce8', border: '1px solid #fde68a',
                      fontSize: '0.78rem', color: '#92400e'
                    }}>
                      No permissions selected — this user won't be able to access any modules.
                    </div>
                  )}
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button type="button" className="detail-btn detail-btn--ghost" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            {showPermissionsTab && activeTab === 'basic' && (
              <button type="button" className="detail-btn detail-btn--secondary" onClick={() => setActiveTab('permissions')}>
                Next: Permissions
              </button>
            )}
            <button type="submit" className="detail-btn detail-btn--primary">
              <FontAwesomeIcon icon={faCheck} />
              {modalMode === 'add' ? 'Create User' : 'Save Changes'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Users;
